"use client"

import { useEffect, useState } from "react"
import { useSearchParams } from "next/navigation"

import Register from "@modules/account/components/register"
import Login from "@modules/account/components/login"

export enum LOGIN_VIEW {
  SIGN_IN = "sign-in",
  REGISTER = "register",
}

const viewFromSearchParams = (searchParams: URLSearchParams) =>
  searchParams.get("view") === "register" ? LOGIN_VIEW.REGISTER : LOGIN_VIEW.SIGN_IN

const LoginTemplate = () => {
  const searchParams = useSearchParams()
  const [currentView, setCurrentView] = useState<LOGIN_VIEW>(() =>
    viewFromSearchParams(searchParams)
  )

  // The header's Sign In / Create an Account links both point at this same
  // route, differing only by the `view` query param. Next.js reuses this
  // component instance across that navigation (no remount), so the tab must
  // be resynced explicitly whenever the URL's view param changes.
  useEffect(() => {
    setCurrentView(viewFromSearchParams(searchParams))
  }, [searchParams])

  return (
    <div className="min-h-screen flex justify-center bg-gray-100 px-4 sm:px-8 pt-8 sm:pt-14 pb-16">
      <div className="w-full max-w-[560px] h-fit bg-white rounded-lg shadow-[0_1px_6px_rgba(0,0,0,0.1)] overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setCurrentView(LOGIN_VIEW.REGISTER)}
            className={`flex-1 py-4 text-[13px] text-center transition-colors border-b-[3px] ${
              currentView === LOGIN_VIEW.REGISTER
                ? "text-brand-dark-navy font-semibold border-[#c41e3a]"
                : "text-gray-500 font-medium border-transparent hover:text-gray-700"
            }`}
          >
            New to UKSS?
          </button>
          <button
            onClick={() => setCurrentView(LOGIN_VIEW.SIGN_IN)}
            className={`flex-1 py-4 text-[13px] text-center transition-colors border-b-[3px] ${
              currentView === LOGIN_VIEW.SIGN_IN
                ? "text-brand-dark-navy font-semibold border-[#c41e3a]"
                : "text-gray-500 font-medium border-transparent hover:text-gray-700"
            }`}
          >
            Already Registered?
          </button>
        </div>

        <div className="px-6 pb-6">
          {currentView === LOGIN_VIEW.SIGN_IN ? (
            <Login setCurrentView={setCurrentView} />
          ) : (
            <Register setCurrentView={setCurrentView} />
          )}
        </div>

        {/* Bottom swap-view CTA */}
        <div className="border-t border-gray-200 px-6 py-5 text-center">
          {currentView === LOGIN_VIEW.SIGN_IN ? (
            <>
              <p className="text-sm text-gray-500 mb-3">
                Don&apos;t have an account?
              </p>
              <button
                onClick={() => setCurrentView(LOGIN_VIEW.REGISTER)}
                className="px-6 py-2.5 border border-brand-dark-navy rounded-full text-[15px] font-semibold text-brand-dark-navy hover:bg-gray-50 transition-colors"
              >
                Create an account
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-3">
                Already have an account?
              </p>
              <button
                onClick={() => setCurrentView(LOGIN_VIEW.SIGN_IN)}
                className="px-6 py-2.5 border border-brand-dark-navy rounded-full text-[15px] font-semibold text-brand-dark-navy hover:bg-gray-50 transition-colors"
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default LoginTemplate
